import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Collapse,
  createTheme,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as locales from "@material-ui/core/locale";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MoreVert,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
} from "@material-ui/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faTrash,
  faRedo,
  faUndo,
  faArrowRight,
  faCopy,
  faClock,
  faHandshake,
  faAddressCard,
} from "@fortawesome/free-solid-svg-icons";
import RutModal from "src/components/Modal/propuestas/Rut-modal";
import ModalParticipaciones from "src/components/Modal/propuestas/Modal-participaciones";
import { formatRut, RutFormat } from "@fdograph/rut-utilities";
import SearchNotFound from "../SearchNotFound";
import { formatearFecha } from "src/utils/formatTime";
import CustomTablePagination from "../CustomTablePagination";
import { capitalizarPalabras, normalize } from "src/utils/sharedFunctions";
import {
  formatValorPlan,
  calcularTotalesPorMoneda,
} from "src/utils/formatNumber";
import { TablaHead } from "../_dashboard/tablas";
import { applySortFilter, getComparator } from "src/utils/sharedTables";
import Scrollbar from "../Scrollbar";
import { propuestasApi } from "src/services/propuestasApi";
import { useSnackbar } from "src/components/snackbar";
import ProposalStatusLabel from "../material/ProposalStatusLabel";
import { formatEstadoPropuesta } from "src/utils/propuestasV2/formatEstadoPropuesta";
import SucursalFilter from "../estadisticas/sucursal-filter";
import { useHistorialVisita } from "src/hooks/useHistorialVisita";
import ModalExtenderPropuesta from "../Modal/propuestas/Modal-extender-propuesta";
import { AlertaModalV2 } from "../AlertaSwall";
import { BRAND } from "src/theme/brandTokens";

const TABLE_HEAD = [
  { id: "position", label: "N°", alignRight: false },
  { id: "tipo", label: "Tipo", alignRight: false },
  { id: "nombreVendedor", label: "Vendedor", alignRight: false },
  { id: "nombreSolicitante", label: "Solicitante", alignRight: false },
  { id: "servicios", label: "Servicios / Valor", alignRight: false },
  { id: "fechaCreacion", label: "F. Creación", alignRight: false },
  { id: "estado", label: "Estado", alignRight: false },
  { id: "accion", label: "Acciones", alignRight: false },
];

const INITIAL_STATE_TABLE = {
  currentPage: 0,
  rowsPerPage: 10,
  order: "desc",
  orderBy: "fechaCreacion",
  locale: "esES",
  filterName: "",
};

const VERSION_CHIPS = ["Todas", "V1", "V2"];

const TIPO_COLORS = {
  PLAN: "primary",
  TAREA: "secondary",
  MIXTA: "warning",
};

const ESTADO_OPTIONS_MAP = {
  V1: [
    "CREADA",
    "ENVIADA",
    "ACEPTADA",
    "ACEPTADO_CLIENTE",
    "RECHAZADA",
    "CADUCADA",
    "MIGRADA",
  ],
  V2: [
    "BORRADOR",
    "PENDIENTE",
    "EN_RENEGOCIACION",
    "ACEPTADA",
    "PARCIALMENTE_ACEPTADA",
    "RECHAZADA",
    "CADUCADA",
  ],
};

const buildEstadoOptions = (versionFilter) => {
  let estados;
  if (versionFilter === "V1") {
    estados = ESTADO_OPTIONS_MAP.V1;
  } else if (versionFilter === "V2") {
    estados = ESTADO_OPTIONS_MAP.V2;
  } else {
    estados = [
      ...new Set([...ESTADO_OPTIONS_MAP.V1, ...ESTADO_OPTIONS_MAP.V2]),
    ];
  }
  return estados.map((e) => ({ value: e, label: formatEstadoPropuesta(e) }));
};

// ── Componente principal ────────────────────────────────────────────────
const TablaPropuestasUnificada = ({ onUploadFile }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { empleado: user } = useSelector((state) => state.auth.user);

  useHistorialVisita(user);

  // ── Filtro de sucursales (persistido en localStorage)
  const [sucursalesSeleccionadas, setSucursalesSeleccionadas] = useState(() => {
    try {
      const saved = localStorage.getItem("propuestasUnificadas_sucursales");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const handleChangeSucursales = useCallback((nuevas) => {
    setSucursalesSeleccionadas(nuevas);
    localStorage.setItem(
      "propuestasUnificadas_sucursales",
      JSON.stringify(nuevas),
    );
  }, []);

  // ── Data fetching
  const { data: propuestas = [], isLoading: loadingTable } =
    propuestasApi.useGetListadoUnificadoQuery({
      sucursales: sucursalesSeleccionadas,
    });

  // ── Estado de tabla
  const [statesTable, setStateTable] = useState(INITIAL_STATE_TABLE);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [versionFilter, setVersionFilter] = useState("Todas");
  const [estadosSeleccionados, setEstadosSeleccionados] = useState([]);

  // ── Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);

  // ── Migración V1 -> V2: indicador por fila (8-20s por la llamada a GPT-4o)
  const [migrandoId, setMigrandoId] = useState(null);

  // ── Duplicación V2: indicador por fila (patrón igual a migrandoId)
  const [duplicandoId, setDuplicandoId] = useState(null);

  // ── Modal Extender/Reactivar (mismo componente, tipo discrimina)
  // Reutiliza ModalExtenderPropuesta ya usado por TablaPropuestas y
  // TablaPropuestasTareasEspeciales en V1. Siguiendo ese patrón:
  // selectedProposal + openModalExtender + tipoAccion.
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [clientesPendientesDialog, setClientesPendientesDialog] =
    useState(null);
  const [openModalExtender, setOpenModalExtender] = useState(false);
  const [tipoAccion, setTipoAccion] = useState(null);

  // ── Aceptación final V1
  const [openModalAcceptation, setOpenModalAcceptation] = useState(false);
  const [openModalCommission, setOpenModalCommission] = useState(false);
  const [dataCommissions, setDataCommissions] = useState({});

  // ── Mutations
  const [eliminarPropuesta] = propuestasApi.useDeletePropuestaMutation();
  const [generarBorradorDesdeMigracion] =
    propuestasApi.useGenerarBorradorDesdeMigracionMutation();
  const [migrarPropuestaV1] = propuestasApi.useMigrarPropuestaV1Mutation();
  const [duplicarPropuesta] = propuestasApi.useDuplicarPropuestaMutation();
  const [fetchPropuestaV1] = propuestasApi.useLazyGetPropuestaQuery();
  const [fetchPlanesPropuesta] =
    propuestasApi.useLazyObtenerPlanesPropuestaQuery();

  // ── Handlers tabla
  const handleFilterByName = useCallback((event) => {
    setStateTable((prev) => ({
      ...prev,
      filterName: event.target.value,
      currentPage: 0,
    }));
  }, []);

  const handleRequestSort = useCallback((event, property) => {
    setStateTable((prev) => {
      const isAsc = prev.orderBy === property && prev.order === "asc";
      return { ...prev, order: isAsc ? "desc" : "asc", orderBy: property };
    });
  }, []);

  const handleChangePage = useCallback((event, newPage) => {
    setStateTable((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setStateTable((prev) => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      currentPage: 0,
    }));
  }, []);

  const estadoOptions = useMemo(
    () => buildEstadoOptions(versionFilter),
    [versionFilter],
  );

  const handleChangeEstados = useCallback((_event, newValue) => {
    setEstadosSeleccionados(newValue);
    setStateTable((prev) => ({ ...prev, currentPage: 0 }));
  }, []);

  const handleToggleExpanded = useCallback((propuestaId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(propuestaId)) {
        newSet.delete(propuestaId);
      } else {
        newSet.add(propuestaId);
      }
      return newSet;
    });
  }, []);

  // ── Menu handlers
  const handleMenuClick = useCallback((event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(id);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedRowId(null);
  }, []);

  // ── Navegación bifurcada
  const navigateToDetail = useCallback(
    (propuesta) => {
      if (propuesta.version === "v2") {
        navigate(`/dashboard/propuestas/v2/${propuesta.id}`);
      } else {
        navigate(`/dashboard/propuestas/${propuesta.tipo}/${propuesta.id}`);
      }
    },
    [navigate],
  );

  // ── Acciones
  const handleEliminar = useCallback(
    async (id) => {
      handleMenuClose();

      const result = await AlertaModalV2({
        title: "¿Eliminar propuesta?",
        text: "Se eliminará la propuesta en estado BORRADOR. Esta acción no se puede deshacer.",
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
        icon: "warning",
      }).fire();

      if (!result.isConfirmed) return;

      try {
        await eliminarPropuesta(id).unwrap();
        enqueueSnackbar("Propuesta eliminada", { variant: "success" });
      } catch (error) {
        enqueueSnackbar(
          error?.data?.message || "Error al eliminar la propuesta",
          { variant: "error" },
        );
      }
    },
    [eliminarPropuesta, enqueueSnackbar, handleMenuClose],
  );

  const handleAbrirReactivar = useCallback(
    (propuesta) => {
      handleMenuClose();
      setSelectedProposal(propuesta);
      setTipoAccion("reactivar");
      setOpenModalExtender(true);
    },
    [handleMenuClose],
  );

  const handleAbrirExtender = useCallback(
    (propuesta) => {
      handleMenuClose();
      setSelectedProposal(propuesta);
      setTipoAccion("extender");
      setOpenModalExtender(true);
    },
    [handleMenuClose],
  );

  // Para V2, enviar/reenviar se realiza desde el detalle de la propuesta
  // donde el usuario tiene acceso al contexto completo antes de confirmar.
  const handleAbrirEnvioV2 = useCallback(
    (propuesta) => {
      handleMenuClose();
      navigate(`/dashboard/propuestas/v2/${propuesta.id}`);
    },
    [handleMenuClose, navigate],
  );

  // ── Migración V1 -> V2 (genera borrador con IA + marca V1 como MIGRADA + navega)
  // El indicador visual se muestra en la fila correspondiente (migrandoId), no globalmente.
  const handleMigrarAV2 = useCallback(
    async (propuesta) => {
      handleMenuClose();
      setMigrandoId(propuesta.id);
      try {
        // 1. Detalle V1 (antecedentes, comentarios, solicitante)
        const detalleV1 = await fetchPropuestaV1({
          tipo: propuesta.tipo,
          id: propuesta.id,
        }).unwrap();
        const detalle = Array.isArray(detalleV1) ? detalleV1[0] : detalleV1;

        // 2. Clientes distintos de los ítems
        let clientes = [];
        if (propuesta.tipo === "PLAN") {
          const planes = await fetchPlanesPropuesta(propuesta.id).unwrap();
          clientes = [
            ...new Set((planes || []).map((p) => p.refCliente).filter(Boolean)),
          ];
        } else if (detalle?.cliente) {
          clientes = [detalle.cliente.rut || detalle.cliente.nombre].filter(
            Boolean,
          );
        }

        // 3. Payload al endpoint de IA (nombres de servicios ya vienen en la fila)
        const payload = {
          antecedentes: detalle?.antecedentes || "",
          comentarios: detalle?.comentarios || "",
          servicios: (propuesta.servicios || [])
            .map((s) => s.nombre)
            .filter(Boolean),
          clientes,
          vendedor: propuesta.nombreVendedor || "",
          sucursal: propuesta.sucursal || undefined,
        };

        const { borrador } =
          await generarBorradorDesdeMigracion(payload).unwrap();

        // 4. Marcar V1 como MIGRADA
        await migrarPropuestaV1({ id: propuesta.id }).unwrap();

        // 5. Navegar al formulario V2 con el borrador + origen
        enqueueSnackbar("Borrador generado", { variant: "success" });
        navigate("/dashboard/propuestas/nueva-v2", {
          state: {
            borradorMigracion: borrador,
            origenMigracion: {
              propuestaId: propuesta.id,
              tipo: propuesta.tipo,
              solicitanteId: detalle?.nombreSolicitante ? "existente" : null,
            },
          },
        });
      } catch (err) {
        enqueueSnackbar(
          err?.data?.message || "Error al migrar la propuesta a V2",
          { variant: "error" },
        );
      } finally {
        setMigrandoId(null);
      }
    },
    [
      fetchPropuestaV1,
      fetchPlanesPropuesta,
      generarBorradorDesdeMigracion,
      migrarPropuestaV1,
      navigate,
      enqueueSnackbar,
      handleMenuClose,
    ],
  );

  // ── Duplicar V2 (estados BORRADOR/ACEPTADA/RECHAZADA/CADUCADA — mismo set que valida el backend)
  // Backend crea nuevo BORRADOR con vendedor=actor y copia atómica de items+equipo.
  // Navega directo a /editar del nuevo BORRADOR, no al detalle solo-lectura.
  const handleDuplicar = useCallback(
    async (propuesta) => {
      handleMenuClose();
      setDuplicandoId(propuesta.id);
      try {
        const { id: nuevoId } = await duplicarPropuesta({
          id: propuesta.id,
        }).unwrap();
        enqueueSnackbar("Propuesta duplicada", { variant: "success" });
        navigate(`/dashboard/propuestas/v2/${nuevoId}/editar`);
      } catch (err) {
        enqueueSnackbar(
          err?.data?.message || "Error al duplicar la propuesta",
          { variant: "error" },
        );
      } finally {
        setDuplicandoId(null);
      }
    },
    [duplicarPropuesta, enqueueSnackbar, navigate, handleMenuClose],
  );

  const handleAceptacionFinal = useCallback(
    (propuesta) => {
      setSelectedProposal(propuesta);
      if (propuesta.tipo === "TAREA") {
        setOpenModalCommission(true);
      } else {
        setOpenModalAcceptation(true);
      }
      handleMenuClose();
    },
    [handleMenuClose],
  );

  const handleCompletarDatos = useCallback(
    (propuesta) => {
      const pendientes = propuesta.clientesPendientes || [];
      if (pendientes.length === 1) {
        navigate(
          `/dashboard/clientes/${encodeURIComponent(
            pendientes[0].rut,
          )}/completar-datos-formales`,
        );
      } else if (pendientes.length > 1) {
        setClientesPendientesDialog(pendientes);
      }
      handleMenuClose();
    },
    [navigate, handleMenuClose],
  );

  const propuestaParaModales = useMemo(() => {
    if (!selectedProposal) return null;
    const servicio = selectedProposal.servicios?.[0] || {};
    return {
      ...selectedProposal,
      nombreTarea: servicio.nombre,
      precioFinal: servicio.valor,
      tipoMoneda: servicio.tipoMoneda,
      vendidoTaxtic: servicio.vendidoTaxtic,
    };
  }, [selectedProposal]);

  // ── Menu options por fila — matriz de acciones por (version, estado)
  const getMenuOptions = useCallback(
    (propuesta) => {
      // V1 CREADA -> solo "Migrar a V2" (el Ver vive en el botón visible fuera del menú)
      if (propuesta.version === "v1" && propuesta.estado === "CREADA") {
        return [
          {
            key: "migrar",
            label: "Migrar a V2",
            icon: faArrowRight,
            action: () => handleMigrarAV2(propuesta),
          },
        ];
      }

      const duplicarOption = {
        key: "duplicar",
        label: "Duplicar propuesta",
        icon: faCopy,
        action: () => handleDuplicar(propuesta),
      };

      const completarDatosOption = {
        key: "completar_datos",
        label: "Completar datos formales",
        icon: faAddressCard,
        color: "warning",
        action: () => handleCompletarDatos(propuesta),
      };

      // V2 BORRADOR -> Enviar + Duplicar + Eliminar
      if (propuesta.version === "v2" && propuesta.estado === "BORRADOR") {
        return [
          {
            key: "enviar",
            label: "Enviar propuesta",
            icon: faPaperPlane,
            action: () => handleAbrirEnvioV2(propuesta),
          },
          duplicarOption,
          {
            key: "eliminar",
            label: "Eliminar propuesta",
            icon: faTrash,
            color: "error",
            action: () => handleEliminar(propuesta.id),
          },
        ];
      }

      // V2 PENDIENTE -> Reenviar + Extender
      if (propuesta.version === "v2" && propuesta.estado === "PENDIENTE") {
        return [
          {
            key: "reenviar",
            label: "Reenviar propuesta",
            icon: faRedo,
            action: () => handleAbrirEnvioV2(propuesta),
          },
          {
            key: "extender",
            label: "Extender vigencia",
            icon: faClock,
            action: () => handleAbrirExtender(propuesta),
          },
        ];
      }

      // V2 ACEPTADA -> Duplicar (+ Completar datos formales si el cliente no tiene correo de facturación)
      if (propuesta.version === "v2" && propuesta.estado === "ACEPTADA") {
        return propuesta.datosFacturacionPendiente &&
          propuesta.clientesPendientes?.length
          ? [duplicarOption, completarDatosOption]
          : [duplicarOption];
      }

      // V2 RECHAZADA -> solo Duplicar
      if (propuesta.version === "v2" && propuesta.estado === "RECHAZADA") {
        return [duplicarOption];
      }

      // V2 CADUCADA -> Duplicar + Reactivar
      if (propuesta.version === "v2" && propuesta.estado === "CADUCADA") {
        return [
          duplicarOption,
          {
            key: "reactivar",
            label: "Reactivar propuesta",
            icon: faUndo,
            action: () => handleAbrirReactivar(propuesta),
          },
        ];
      }

      // V2 PARCIALMENTE_ACEPTADA -> Completar datos formales si pendiente (sino solo Ver)
      if (
        propuesta.version === "v2" &&
        propuesta.estado === "PARCIALMENTE_ACEPTADA"
      ) {
        return propuesta.datosFacturacionPendiente &&
          propuesta.clientesPendientes?.length
          ? [completarDatosOption]
          : [];
      }

      if (
        propuesta.version === "v1" &&
        propuesta.estado === "ACEPTADO_CLIENTE"
      ) {
        return [
          {
            key: "aceptacion_final",
            label: "Aceptación final",
            icon: faHandshake,
            color: "success",
            action: () => handleAceptacionFinal(propuesta),
          },
        ];
      }

      // Resto (V1 enviadas/aceptadas/rechazadas/caducadas/migradas, V2 parcialmente_aceptada) -> solo Ver
      return [];
    },
    [
      handleAbrirEnvioV2,
      handleMigrarAV2,
      handleDuplicar,
      handleEliminar,
      handleAbrirReactivar,
      handleAbrirExtender,
      handleAceptacionFinal,
      handleCompletarDatos,
    ],
  );

  // ── Filtrado
  const propuestasFiltradas = useMemo(() => {
    let filtered = propuestas;

    if (versionFilter === "V1") {
      filtered = filtered.filter((p) => p.version === "v1");
    } else if (versionFilter === "V2") {
      filtered = filtered.filter((p) => p.version === "v2");
    }

    if (estadosSeleccionados.length > 0) {
      const estadosValues = estadosSeleccionados.map((e) => e.value);
      filtered = filtered.filter((p) => estadosValues.includes(p.estado));
    }

    const filterTerm = normalize(statesTable.filterName);
    if (filterTerm) {
      filtered = filtered.filter((p) => {
        const fields = [
          p.nombreVendedor,
          p.nombreSolicitante,
          p.correoSolicitante,
          p.estado,
        ];
        const matchFields = fields.some((f) =>
          normalize(f)?.includes(filterTerm),
        );
        const matchServicios = p.servicios?.some((s) =>
          normalize(s.nombre)?.includes(filterTerm),
        );
        return matchFields || matchServicios;
      });
    }

    return applySortFilter(
      filtered,
      getComparator(statesTable.order, statesTable.orderBy),
      "",
      [],
    );
  }, [propuestas, versionFilter, estadosSeleccionados, statesTable]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(propuestasFiltradas.length / statesTable.rowsPerPage) - 1,
    );
    if (statesTable.currentPage > maxPage) {
      setStateTable((prev) => ({ ...prev, currentPage: maxPage }));
    }
  }, [
    propuestasFiltradas.length,
    statesTable.rowsPerPage,
    statesTable.currentPage,
  ]);

  const isNotFound =
    !loadingTable && propuestasFiltradas.length === 0 && statesTable.filterName;

  const emptyRows =
    statesTable.currentPage > 0
      ? Math.max(
          0,
          (1 + statesTable.currentPage) * statesTable.rowsPerPage -
            propuestasFiltradas.length,
        )
      : 0;

  // ── Render
  return (
    <Card sx={{ mt: 2 }}>
      {/* ── Cabecera con filtros ──────────────────────────────────── */}
      <Grid container spacing={2} sx={{ p: 2 }} alignItems="center">
        <Grid item xs={12} md={4}>
          <CardHeader
            title="Propuestas"
            subheader={`${propuestasFiltradas.length} propuesta${
              propuestasFiltradas.length !== 1 ? "s" : ""
            }`}
            sx={{ p: 0 }}
          />
        </Grid>

        {/* Chips de versión */}
        <Grid item xs={12} md={8}>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            {VERSION_CHIPS.map((v) => (
              <Chip
                key={v}
                label={v}
                size="small"
                color={versionFilter === v ? "primary" : "default"}
                variant={versionFilter === v ? "filled" : "outlined"}
                onClick={() => {
                  setVersionFilter(v);
                  setEstadosSeleccionados([]);
                }}
                sx={{ cursor: "pointer" }}
              />
            ))}
          </Box>
        </Grid>

        {/* Buscador */}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Buscar por vendedor, solicitante, servicio..."
            value={statesTable.filterName}
            onChange={handleFilterByName}
          />
        </Grid>

        {/* Filtro estados */}
        <Grid item xs={12} md={4}>
          <Autocomplete
            multiple
            size="small"
            options={estadoOptions}
            value={estadosSeleccionados}
            onChange={handleChangeEstados}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  {...getTagProps({ index })}
                  size="small"
                  color="primary"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={
                  estadosSeleccionados.length === 0
                    ? "Filtrar por estado..."
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Filtro sucursales */}
        <Grid item xs={12} md={4}>
          <SucursalFilter
            rutEmpleado={user?.rut}
            sucursalesSeleccionadas={sucursalesSeleccionadas}
            onChangeSucursales={handleChangeSucursales}
            tamanio={12}
            label=""
            size="small"
          />
        </Grid>
      </Grid>

      {/* ── Tabla ─────────────────────────────────────────────────── */}
      <Scrollbar>
        <TableContainer>
          <Table>
            <TablaHead
              order={statesTable.order}
              orderBy={statesTable.orderBy}
              headLabel={TABLE_HEAD}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {loadingTable
                ? [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {TABLE_HEAD.map((col) => (
                        <TableCell key={col.id}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : propuestasFiltradas
                    .slice(
                      statesTable.currentPage * statesTable.rowsPerPage,
                      statesTable.currentPage * statesTable.rowsPerPage +
                        statesTable.rowsPerPage,
                    )
                    .map((propuesta, index) => {
                      const isExpanded = expandedRows.has(propuesta.id);
                      const totalesPorMoneda = calcularTotalesPorMoneda(
                        propuesta.servicios,
                      );

                      const enMigracion = migrandoId === propuesta.id;
                      const enDuplicacion = duplicandoId === propuesta.id;
                      const filaBloqueada = enMigracion || enDuplicacion;

                      return (
                        <React.Fragment
                          key={`${propuesta.version}-${propuesta.id}`}
                        >
                          {/* Fila principal */}
                          <TableRow
                            hover
                            sx={{
                              "& > td": {
                                borderBottom: isExpanded ? "none" : undefined,
                              },
                              ...(filaBloqueada && {
                                opacity: 0.6,
                                pointerEvents: "none",
                              }),
                            }}
                          >
                            {/* N° */}
                            <TableCell sx={{ width: 50 }}>
                              <Typography variant="body2" color="textSecondary">
                                {statesTable.currentPage *
                                  statesTable.rowsPerPage +
                                  index +
                                  1}
                              </Typography>
                            </TableCell>

                            {/* Tipo + Versión */}
                            <TableCell sx={{ width: 130 }}>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Chip
                                  label={propuesta.tipo}
                                  size="small"
                                  color={
                                    TIPO_COLORS[propuesta.tipo] || "default"
                                  }
                                  variant="outlined"
                                />
                                <Chip
                                  label={propuesta.version.toUpperCase()}
                                  size="small"
                                  variant="filled"
                                  sx={{
                                    fontSize: "0.65rem",
                                    height: 20,
                                    bgcolor:
                                      propuesta.version === "v2"
                                        ? BRAND.color.primary
                                        : "grey.400",
                                    color: "white",
                                  }}
                                />
                                {propuesta.datosFacturacionPendiente && (
                                  <Tooltip
                                    title={`${
                                      propuesta.clientesPendientes?.length || 1
                                    } cliente(s) sin correo de facturación`}
                                  >
                                    <Chip
                                      label="Facturación"
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ fontSize: "0.6rem", height: 20 }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>

                            {/* Vendedor */}
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    bgcolor: "secondary.light",
                                  }}
                                >
                                  <BusinessIcon sx={{ fontSize: 16 }} />
                                </Avatar>
                                <Typography variant="body2">
                                  {capitalizarPalabras(
                                    propuesta.nombreVendedor || "N/A",
                                  )}
                                </Typography>
                              </Box>
                            </TableCell>

                            {/* Solicitante */}
                            <TableCell
                              onClick={() => navigateToDetail(propuesta)}
                              style={{ cursor: "pointer" }}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    bgcolor: "primary.light",
                                  }}
                                >
                                  <PersonIcon sx={{ fontSize: 16 }} />
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {capitalizarPalabras(
                                      propuesta.nombreSolicitante || "N/A",
                                    )}
                                  </Typography>
                                  {propuesta.rutSolicitante ? (
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      {formatRut(
                                        propuesta.rutSolicitante,
                                        RutFormat.DOTS_DASH,
                                      )}
                                    </Typography>
                                  ) : propuesta.correoSolicitante ? (
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      {propuesta.correoSolicitante}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Box>
                            </TableCell>

                            {/* Servicios / Valor */}
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Tooltip
                                  title={
                                    isExpanded
                                      ? "Ocultar detalle"
                                      : "Ver detalle de servicios"
                                  }
                                >
                                  <Chip
                                    label={`${propuesta.servicios?.length || 0} servicio${
                                      (propuesta.servicios?.length || 0) !== 1
                                        ? "s"
                                        : ""
                                    }`}
                                    size="small"
                                    color="info"
                                    variant={isExpanded ? "filled" : "outlined"}
                                    onClick={() =>
                                      handleToggleExpanded(propuesta.id)
                                    }
                                    onDelete={() =>
                                      handleToggleExpanded(propuesta.id)
                                    }
                                    deleteIcon={
                                      isExpanded ? (
                                        <ExpandLessIcon sx={{ fontSize: 16 }} />
                                      ) : (
                                        <ExpandMoreIcon sx={{ fontSize: 16 }} />
                                      )
                                    }
                                    sx={{ cursor: "pointer" }}
                                  />
                                </Tooltip>
                                <Box>
                                  {totalesPorMoneda.length === 0 ? (
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      S/P
                                    </Typography>
                                  ) : totalesPorMoneda.length === 1 ? (
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                    >
                                      {formatValorPlan(
                                        totalesPorMoneda[0].total,
                                        totalesPorMoneda[0].moneda,
                                      )}
                                    </Typography>
                                  ) : (
                                    totalesPorMoneda.map((t) => (
                                      <Typography
                                        key={t.moneda}
                                        variant="caption"
                                        display="block"
                                        fontWeight="bold"
                                      >
                                        {formatValorPlan(t.total, t.moneda)}
                                      </Typography>
                                    ))
                                  )}
                                </Box>
                              </Box>
                            </TableCell>

                            {/* Fecha Creación */}
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {formatearFecha(
                                  propuesta.fechaCreacion,
                                  "DD/MM/YYYY",
                                )}
                              </Typography>
                            </TableCell>

                            {/* Estado */}
                            <TableCell>
                              <ProposalStatusLabel estado={propuesta.estado} />
                            </TableCell>

                            {/* Acciones */}
                            <TableCell align="center" sx={{ width: 100 }}>
                              {filaBloqueada ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                    color: BRAND.color.primary,
                                    opacity: 1,
                                    pointerEvents: "auto",
                                  }}
                                >
                                  <CircularProgress
                                    size={16}
                                    sx={{ color: BRAND.color.primary }}
                                  />
                                  <Typography
                                    variant="caption"
                                    fontWeight="medium"
                                  >
                                    {enMigracion
                                      ? "Migrando a V2…"
                                      : "Duplicando…"}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <Tooltip title="Ver detalle">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        navigateToDetail(propuesta)
                                      }
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {(() => {
                                    const menuOptions =
                                      getMenuOptions(propuesta);
                                    if (menuOptions.length === 0) return null;
                                    return (
                                      <>
                                        <Tooltip title="Más opciones">
                                          <IconButton
                                            size="small"
                                            onClick={(e) =>
                                              handleMenuClick(e, propuesta.id)
                                            }
                                          >
                                            <MoreVert fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Menu
                                          anchorEl={anchorEl}
                                          open={
                                            Boolean(anchorEl) &&
                                            selectedRowId === propuesta.id
                                          }
                                          onClose={handleMenuClose}
                                          transformOrigin={{
                                            horizontal: "right",
                                            vertical: "top",
                                          }}
                                          anchorOrigin={{
                                            horizontal: "right",
                                            vertical: "bottom",
                                          }}
                                          PaperProps={{
                                            style: { minWidth: "200px" },
                                          }}
                                        >
                                          {menuOptions.map((opt) => (
                                            <MenuItem
                                              key={opt.key}
                                              onClick={opt.action}
                                              disabled={opt.disabled}
                                            >
                                              <ListItemIcon>
                                                <FontAwesomeIcon
                                                  icon={opt.icon}
                                                  style={{
                                                    color:
                                                      opt.color === "error"
                                                        ? "#f44336"
                                                        : opt.color ===
                                                            "success"
                                                          ? "#4caf50"
                                                          : undefined,
                                                  }}
                                                />
                                              </ListItemIcon>
                                              <ListItemText>
                                                {opt.label}
                                              </ListItemText>
                                            </MenuItem>
                                          ))}
                                        </Menu>
                                      </>
                                    );
                                  })()}
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>

                          {/* Fila expandible */}
                          <TableRow>
                            <TableCell
                              colSpan={TABLE_HEAD.length}
                              sx={{ py: 0, px: 0 }}
                            >
                              <Collapse
                                in={isExpanded}
                                timeout="auto"
                                unmountOnExit
                              >
                                <DetalleServicios propuesta={propuesta} />
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}

              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={TABLE_HEAD.length} />
                </TableRow>
              )}
            </TableBody>

            {isNotFound && (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={TABLE_HEAD.length}>
                    <SearchNotFound searchQuery={statesTable.filterName} />
                  </TableCell>
                </TableRow>
              </TableBody>
            )}

            <TableFooter>
              <TableRow>
                <ThemeProvider
                  theme={(outerTheme) =>
                    createTheme(outerTheme, locales[statesTable.locale])
                  }
                >
                  <CustomTablePagination
                    colSpan={TABLE_HEAD.length}
                    count={propuestasFiltradas.length}
                    rowsPerPage={statesTable.rowsPerPage}
                    page={statesTable.currentPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </ThemeProvider>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Scrollbar>

      {openModalExtender && (
        <ModalExtenderPropuesta
          open={openModalExtender}
          setOpen={setOpenModalExtender}
          propuesta={selectedProposal}
          tipo={tipoAccion}
        />
      )}

      <RutModal
        open={openModalAcceptation}
        setOpen={setOpenModalAcceptation}
        id={selectedProposal?.id}
        rutAuxiliar={!!selectedProposal?.rutAuxiliar}
        tipoPropuesta={selectedProposal?.tipo}
        dataCommissions={dataCommissions}
        rutProvisorio={selectedProposal?.rutCliente}
        faltaCorreoFacturacion={!selectedProposal?.correoFacturacionCliente}
      />

      {openModalCommission && (
        <ModalParticipaciones
          open={openModalCommission}
          setOpen={setOpenModalCommission}
          fullWidth
          maxWidth="lg"
          propuesta={propuestaParaModales}
          setOpenModalAcceptation={setOpenModalAcceptation}
          setDataCommissions={setDataCommissions}
        />
      )}

      <Dialog
        open={Boolean(clientesPendientesDialog)}
        onClose={() => setClientesPendientesDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Clientes sin correo de facturación</DialogTitle>
        <DialogContent dividers>
          {(clientesPendientesDialog || []).map((c) => (
            <Box
              key={c.rut}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ py: 1, gap: 2 }}
            >
              <Box>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {c.nombre}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  RUT: {c.rut}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setClientesPendientesDialog(null);
                  navigate(
                    `/dashboard/clientes/${encodeURIComponent(
                      c.rut,
                    )}/completar-datos-formales`,
                  );
                }}
              >
                Completar
              </Button>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientesPendientesDialog(null)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// ── Detalle expandible de servicios ──────────────────────────────────────
const DetalleServicios = React.memo(({ propuesta }) => {
  if (!propuesta.servicios || propuesta.servicios.length === 0) {
    return (
      <Box sx={{ py: 2, px: 3 }}>
        <Typography variant="body2" color="textSecondary">
          Sin servicios registrados
        </Typography>
      </Box>
    );
  }

  const totales = calcularTotalesPorMoneda(propuesta.servicios);

  const titulo =
    propuesta.version === "v2"
      ? "Items de la propuesta"
      : propuesta.tipo === "PLAN"
        ? "Planes incluidos"
        : "Servicio";

  return (
    <Box sx={{ bgcolor: "grey.50", py: 2, px: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        {titulo}
      </Typography>

      <Table size="small" sx={{ bgcolor: "background.paper", borderRadius: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell>Servicio</TableCell>
            <TableCell>Encargado</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell align="right">Valor original</TableCell>
            <TableCell align="right">Dto.</TableCell>
            <TableCell align="right">Valor final</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {propuesta.servicios.map((s, i) => {
            const tieneDescuento =
              s.descuento != null && Number(s.descuento) > 0;
            const esRecurrente =
              s.recurrente === "SI" ||
              s.recurrente === 1 ||
              s.recurrente === true;
            const frecuenciaLabel = s.tipoFrecuencia
              ? s.tipoFrecuencia === "MES"
                ? "Mensual"
                : s.tipoFrecuencia === "AÑO"
                  ? "Anual"
                  : s.tipoFrecuencia
              : null;

            return (
              <TableRow key={i}>
                {/* Servicio */}
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {capitalizarPalabras(s.nombre || "Sin nombre")}
                    </Typography>
                    {s.vendidoTaxtic === 1 && (
                      <Chip
                        label="Vendido Taxtic"
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ mt: 0.5, height: 20, fontSize: "0.65rem" }}
                      />
                    )}
                    {s.fechaLimite && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        Plazo: {formatearFecha(s.fechaLimite, "DD/MM/YYYY")}
                      </Typography>
                    )}
                  </Box>
                </TableCell>

                {/* Encargado */}
                <TableCell>
                  <Typography
                    variant="body2"
                    color={s.encargado ? "textPrimary" : "textSecondary"}
                  >
                    {s.encargado
                      ? capitalizarPalabras(s.encargado)
                      : "Sin asignar"}
                  </Typography>
                </TableCell>

                {/* Tipo (recurrente / puntual) */}
                <TableCell>
                  {s.tipoItem === "PLAN" || esRecurrente ? (
                    <Box>
                      <Chip
                        label={frecuenciaLabel || "Recurrente"}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 22, fontSize: "0.7rem" }}
                      />
                    </Box>
                  ) : (
                    <Chip
                      label="Puntual"
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: "0.7rem" }}
                    />
                  )}
                </TableCell>

                {/* Valor original */}
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color={tieneDescuento ? "textSecondary" : "textPrimary"}
                    sx={{
                      textDecoration: tieneDescuento ? "line-through" : "none",
                    }}
                  >
                    {formatValorPlan(s.valorOriginal, s.tipoMoneda)}
                  </Typography>
                </TableCell>

                {/* Descuento */}
                <TableCell align="right">
                  {tieneDescuento ? (
                    <Chip
                      label={`-${Number(s.descuento)}%`}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ height: 22, fontSize: "0.7rem" }}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      —
                    </Typography>
                  )}
                </TableCell>

                {/* Valor final */}
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {formatValorPlan(s.valor, s.tipoMoneda)}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}

          {/* Fila(s) de total */}
          {propuesta.servicios.length > 1 &&
            totales.map((t) => (
              <TableRow key={`total-${t.moneda}`}>
                <TableCell colSpan={5} align="right">
                  <Typography variant="subtitle2">
                    {totales.length > 1 ? `Total ${t.moneda}` : "Total"}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" fontWeight="bold">
                    {formatValorPlan(t.total, t.moneda)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      <Divider sx={{ mt: 2 }} />
    </Box>
  );
});

DetalleServicios.displayName = "DetalleServicios";

export default React.memo(TablaPropuestasUnificada);
